import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CONTACT_EMAIL = 'KrypTehKnightCS@gmail.com';
const SITE = 'https://bigplays.gg';

function LegalShell({ title, description, path, children }) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <Helmet>
        <title>{title} - BIGPLAYS.GG</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${title} - BIGPLAYS.GG`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`${SITE}${path}`} />
        <meta property="og:type" content="website" />
      </Helmet>
      <article className="text-gray-200 leading-relaxed">
        {children}
      </article>
    </div>
  );
}

const h2 = 'text-lg font-semibold text-white mt-8 mb-3';
const p = 'mb-4 text-gray-300';
const ul = 'list-disc pl-5 mb-4 space-y-1 text-gray-300';

export const Privacy = () => (
  <LegalShell
    title="Privacy Policy"
    description="How BIGPLAYS.GG collects, uses, stores, and protects account and Google user data, including encryption and access controls for sensitive data."
    path="/privacy"
  >
    <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
    <p className="text-sm text-gray-400 mb-8">
      Effective date: August 31, 2026 · Site: <a className="text-blue-400 hover:underline" href={SITE}>{SITE}</a>
    </p>

    <p className={p}>
      BIGPLAYS.GG (“BIGPLAYS”, “we”, “us”) is a Counter-Strike 2 highlight community with
      shared halls, a live TV, and optional voice. This policy explains what we collect,
      why, and how we use Google user data. It applies to the website at {SITE}.
      The optional Chrome mute-hotkey extension has a separate policy at{' '}
      <a className="text-blue-400 hover:underline" href="/extension-privacy.html">
        /extension-privacy.html
      </a>.
    </p>

    <h2 className={h2}>Google user data (Limited Use)</h2>
    <p className={p}>
      BIGPLAYS’s use and transfer of information received from Google APIs adheres to the{' '}
      <a
        className="text-blue-400 hover:underline"
        href="https://developers.google.com/terms/api-services-user-data-policy"
        target="_blank"
        rel="noopener noreferrer"
      >
        Google API Services User Data Policy
      </a>
      , including the Limited Use requirements. We use Google data only to provide and
      improve BIGPLAYS features described here. We do not sell it, do not use it for
      ads, and do not share it with third parties except as needed to run the service
      (hosting and database). We do not use Google user data to develop, improve, or
      train generalized or non-personalized AI or ML models.
    </p>

    <h2 className={h2}>What Google user data we access</h2>
    <ul className={ul}>
      <li>
        <strong className="text-white">Google Sign-In</strong> (<code>openid</code>,{' '}
        <code>email</code>, <code>profile</code>): Google account ID, email address,
        display name, and profile photo. We use these to create or sign in to your
        BIGPLAYS account and show your avatar.
      </li>
      <li>
        <strong className="text-white">Connect YouTube</strong> (optional;{' '}
        <code>https://www.googleapis.com/auth/youtube.readonly</code>): your YouTube
        channel ID and a refresh token so we can see whether that channel is live.
        We use this only for Connect YouTube and I’m Live (to put your live stream
        on your hall TV). We do not upload, edit, delete, or manage your YouTube
        videos, comments, or playlists.
      </li>
      <li>
        <strong className="text-white">Steam</strong> (optional): Steam ID and avatar
        if you link Steam after signing in with Google.
      </li>
      <li>
        <strong className="text-white">Account and hall data</strong>: username, submitted
        highlight URLs, votes, XP, hall membership, and hall moderation state
        (mute, timeout, ban, role).
      </li>
      <li>
        <strong className="text-white">Session</strong>: a signed cookie so you stay
        logged in.
      </li>
      <li>
        <strong className="text-white">Device storage</strong>: guest XP, per-player
        mute/volume preferences, and video playback position in your browser.
      </li>
      <li>
        <strong className="text-white">Analytics</strong>: Google Analytics 4 records
        page views on BIGPLAYS. It does not receive your YouTube refresh token.
      </li>
    </ul>
    <p className={p}>
      Voice in halls is peer-to-peer in the browser. We do not record or store hall
      voice audio on our servers.
    </p>

    <h2 className={h2}>How we use Google user data</h2>
    <p className={p}>
      We use Google Sign-In data to authenticate you, create or recover your BIGPLAYS
      account, and display your name and photo on the site. We use the optional
      YouTube readonly grant only to identify your channel and check whether it is
      live so we can put that stream on your hall TV when you click I’m Live. We do
      not use Google user data for advertising, retargeting, credit decisions, or
      resale.
    </p>

    <h2 className={h2}>Sharing, transfer, and disclosure of Google user data</h2>
    <p className={p}>
      We do not sell, rent, or share Google user data with advertisers, data brokers,
      or information resellers. We do not transfer or disclose your Google user data
      to third parties for purposes other than providing or improving BIGPLAYS.
      The only processors that may store this data are our hosting and database
      providers (currently Heroku / PostgreSQL) that run the site. Other hall
      visitors can see the public profile details listed below; they cannot see your
      Google password, email (unless you choose to show it), or YouTube tokens.
    </p>

    <h2 className={h2}>Data protection mechanisms for sensitive data</h2>
    <p className={p}>
      Security procedures are in place to protect the confidentiality of your data.
      We use encryption to protect your information.
    </p>
    <ul className={ul}>
      <li>
        <strong className="text-white">Encryption in transit:</strong> all traffic
        between your browser and BIGPLAYS is encrypted with HTTPS/TLS.
      </li>
      <li>
        <strong className="text-white">Encryption at rest:</strong> account records,
        including Google account identifiers and YouTube refresh tokens, are stored
        in PostgreSQL. Our hosting provider encrypts that database storage at rest.
      </li>
      <li>
        <strong className="text-white">Access control:</strong> YouTube refresh tokens
        and Google API credentials stay on the server. They are stripped from API
        responses and are not sent to the browser, hall chat, or public profiles.
        Only the BIGPLAYS application (and operators of the production environment)
        can use them, and only to provide Connect YouTube / I’m Live.
      </li>
      <li>
        <strong className="text-white">Session security:</strong> sign-in is kept in a
        signed session cookie. You can end a session by logging out.
      </li>
      <li>
        <strong className="text-white">Least privilege:</strong> we request only
        <code>openid</code>, <code>email</code>, <code>profile</code> for sign-in and
        optional <code>youtube.readonly</code> for live detection. We do not request
        scopes that upload, edit, or delete YouTube content.
      </li>
    </ul>

    <h2 className={h2}>What other people can see</h2>
    <p className={p}>
      In a hall, other visitors can see your username, avatar, character, and
      submitted highlights on the TV. Profile pages show public account details
      you have on BIGPLAYS, not your Google password and not your YouTube refresh
      token.
    </p>

    <h2 className={h2}>Where data is stored</h2>
    <p className={p}>
      Account data is stored in our PostgreSQL database on our hosting provider
      (currently Heroku). YouTube refresh tokens are stored so Connect YouTube
      keeps working until you disconnect YouTube or we delete the account.
    </p>

    <h2 className={h2}>Data retention and deletion</h2>
    <p className={p}>
      We retain your personal information for the length of time needed to fulfill
      the purposes in this policy unless a longer period is required or permitted
      by law. We keep account and hall data while your account exists. YouTube
      refresh tokens are kept only while YouTube is connected. Guest XP and local
      preferences stay in your browser until you clear site data. Server session
      cookies expire when the session ends or you log out. When we delete an
      account or you disconnect YouTube, we delete or destroy the related Google
      tokens and identifiers we no longer need.
    </p>

    <h2 className={h2}>Your choices</h2>
    <ul className={ul}>
      <li>Disconnect YouTube or Steam on your profile.</li>
      <li>Revoke BIGPLAYS in your Google Account permissions at any time.</li>
      <li>Log out to end the current session.</li>
      <li>
        Email {CONTACT_EMAIL} to ask questions, request a copy of your account
        data, or request deletion.
      </li>
    </ul>

    <h2 className={h2}>Children</h2>
    <p className={p}>
      BIGPLAYS is not directed at children under 13. We do not knowingly collect
      personal information from children under 13.
    </p>

    <h2 className={h2}>Changes</h2>
    <p className={p}>
      If this policy changes, we will post the update at{' '}
      <Link className="text-blue-400 hover:underline" to="/privacy">
        {SITE}/privacy
      </Link>{' '}
      with a new effective date.
    </p>

    <h2 className={h2}>Contact</h2>
    <p className={p}>
      Questions about this policy or Google user data:{' '}
      <a className="text-blue-400 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
        {CONTACT_EMAIL}
      </a>
      .
    </p>

    <p className="mt-10 text-sm text-gray-500">
      <Link className="text-blue-400 hover:underline" to="/">Home</Link>
      {' · '}
      <Link className="text-blue-400 hover:underline" to="/terms">Terms of Service</Link>
      {' · '}
      <Link className="text-blue-400 hover:underline" to="/report">Report</Link>
    </p>
  </LegalShell>
);

export const Terms = () => (
  <LegalShell
    title="Terms of Service"
    description="Terms for using BIGPLAYS.GG."
    path="/terms"
  >
    <h1 className="text-3xl font-bold text-white mb-2">Terms of Service</h1>
    <p className="text-sm text-gray-400 mb-8">
      Effective date: September 11, 2026 · Site: <a className="text-blue-400 hover:underline" href={SITE}>{SITE}</a>
    </p>

    <p className={p}>
      By using BIGPLAYS.GG you agree to these terms and our{' '}
      <Link className="text-blue-400 hover:underline" to="/privacy">Privacy Policy</Link>.
      If you do not agree, do not use the site.
    </p>

    <h2 className={h2}>The service</h2>
    <p className={p}>
      BIGPLAYS is a live hall platform. Halls have a shared YouTube TV. Creator
      halls may play videos from that creator’s YouTube channel. Features may
      change, break, or stop. The site is provided as-is, without warranties.
    </p>

    <h2 className={h2}>Accounts</h2>
    <p className={p}>
      You sign in with Google. Steam and YouTube linking are optional. You are
      responsible for activity on your account.
    </p>

    <h2 className={h2}>Payments</h2>
    <p className={p}>
      Fans may buy digital live-interaction products (for example a VIP pass
      with credits) from a creator’s hall. Payments are processed by Stripe.
      Creators are paid through Stripe Connect. BIGPLAYS keeps a platform fee.
      We may refuse, refund, or reverse a sale that violates these terms.
    </p>

    <h2 className={h2}>Acceptable use</h2>
    <p className={p}>
      You may not post, host, link to, or sell (including VIP passes or other
      paid live interactions) content in these categories:
    </p>
    <ul className={ul}>
      <li>
        Adult content and services: nudity or explicit sexual acts, including
        subscriber-only or paid adult content.
      </li>
      <li>
        Intellectual property or proprietary rights infringement, including
        copyright-infringing content.
      </li>
      <li>
        Violent extremism: content that engages in, encourages, promotes, or
        celebrates unlawful violence or hate speech toward any group based on
        race, religion, disability, gender, sexual orientation, national origin,
        or any other immutable characteristic.
      </li>
    </ul>
    <p className={p}>
      You may only submit or play YouTube videos you have the right to share.
      Halls may show those embeds to other visitors.
    </p>

    <h2 className={h2}>Enforcement and repeat violators</h2>
    <p className={p}>
      We may remove content, disable monetization, restrict hall access (kick,
      timeout, mute, ban), suspend, or ban accounts that violate these terms.
      Repeat or serious violations lead to a lasting ban. We review reports and
      remove violating content when we become aware of it.
    </p>

    <h2 className={h2}>Reports</h2>
    <p className={p}>
      Users, rights holders, and other aggrieved parties can report content that
      violates these terms and request removal at{' '}
      <Link className="text-blue-400 hover:underline" to="/report">{SITE}/report</Link>
      {' '}or{' '}
      <a className="text-blue-400 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
        {CONTACT_EMAIL}
      </a>
      . Include the hall or video URL and why it violates these terms.
    </p>

    <h2 className={h2}>Google and YouTube</h2>
    <p className={p}>
      Google Sign-In and Connect YouTube are subject to Google’s terms. Connect
      YouTube is optional and only used to read whether your channel is live.
    </p>

    <h2 className={h2}>Contact</h2>
    <p className={p}>
      <a className="text-blue-400 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
        {CONTACT_EMAIL}
      </a>
    </p>

    <p className="mt-10 text-sm text-gray-500">
      <Link className="text-blue-400 hover:underline" to="/">Home</Link>
      {' · '}
      <Link className="text-blue-400 hover:underline" to="/privacy">Privacy Policy</Link>
      {' · '}
      <Link className="text-blue-400 hover:underline" to="/report">Report</Link>
    </p>
  </LegalShell>
);

const REPORT_CATEGORIES = [
  { value: 'adult', label: 'Adult content' },
  { value: 'copyright', label: 'Copyright or IP infringement' },
  { value: 'extremism', label: 'Violent extremism or hate' },
  { value: 'other', label: 'Other terms violation' }
];

export const Report = () => {
  const [pageUrl, setPageUrl] = useState('');
  const [category, setCategory] = useState('copyright');
  const [details, setDetails] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/report', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageUrl, category, details, contactEmail })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not send the report');
        return;
      }
      setDone(true);
    } catch {
      setError('Could not send the report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <LegalShell
      title="Report content"
      description="Report content on BIGPLAYS.GG that violates the Terms of Service."
      path="/report"
    >
      <h1 className="text-3xl font-bold text-white mb-2">Report content</h1>
      <p className="text-sm text-gray-400 mb-8">
        Users, rights holders, and others can request removal of content that
        violates our{' '}
        <Link className="text-blue-400 hover:underline" to="/terms">Terms of Service</Link>
        .
      </p>

      {done ? (
        <p className={p}>
          Thanks. We received the report and will review it. You can also email{' '}
          <a className="text-blue-400 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 max-w-xl">
          <label className="block text-sm text-gray-300">
            Page or video URL
            <input
              type="url"
              required
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
              placeholder="https://bigplays.gg/@username"
              className="mt-1 w-full rounded-md bg-gray-900 border border-gray-700 px-3 py-2 text-white"
            />
          </label>
          <label className="block text-sm text-gray-300">
            Category
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full rounded-md bg-gray-900 border border-gray-700 px-3 py-2 text-white"
            >
              {REPORT_CATEGORIES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-gray-300">
            Details
            <textarea
              required
              minLength={10}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={5}
              className="mt-1 w-full rounded-md bg-gray-900 border border-gray-700 px-3 py-2 text-white"
            />
          </label>
          <label className="block text-sm text-gray-300">
            Your email (optional)
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="mt-1 w-full rounded-md bg-gray-900 border border-gray-700 px-3 py-2 text-white"
            />
          </label>
          {error ? <p className="text-sm text-red-400">{error}</p> : null}
          <button
            type="submit"
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-4 py-2 rounded-md disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Submit report'}
          </button>
        </form>
      )}

      <p className="mt-10 text-sm text-gray-500">
        <Link className="text-blue-400 hover:underline" to="/">Home</Link>
        {' · '}
        <Link className="text-blue-400 hover:underline" to="/terms">Terms of Service</Link>
      </p>
    </LegalShell>
  );
};
